import React, { useEffect, useState } from 'react';
import { Container, ButtonContainer, BackButton, Submit } from './styles';
import { Icon, sendCustomTrackingInfo } from '@panel/commons';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { StepperSelectors, StepperOperations } from '../../ducks/stepper';
import { getStepInfo } from '../../utils/stageNavigationUtils';
import { useHistory } from 'react-router-dom';
import { getDevice, isBrasilSite } from '@panel/commons/helpers';
import { Modal } from '@panel/commons/lib/Modals/ModalWrapper';
import { SaveChangeForm } from '../modals/SaveChangeForm';
import { SavePostingSelectors, SavePostingOperations } from '../../ducks/savePosting';
import { triggerSources } from '../../constants/triggerSources';
import { updateLastStep } from '../../ducks/stepper/actions';
import { FormGuardOperations, FormGuardSelectors } from '../../ducks/formGuard';
import { useQuery } from '@panel/commons/hooks';
import { useLocation } from 'react-router-dom';
import CustomPrompt from '../CustomPrompt';
import { LoadingError } from '../modals/LoadingError';
import adaptStepDataAndSendTrackinInfo, {
  sendModalTrackingInfo,
  setStepTitle
} from '../../utils/adaptStepDataAndSendTrackinInfo';
import { setCookieWithTTL } from '@panel/commons/helpers/cookieHelper';

const ButtonPanel = ({
  onSubmit = () => {},
  id = '',
  showBackButton = true,
  btnSubmitText,
  loadError,
  resetInfo,
  device,
  mainData = {},
  planName = '',
  isPlanEmpty,
  getIsValidForm,
  selectedOperationType,
  selectedPropertyType,
  enableBackButton,
  disabledSubmit,
  selectedPropertySubType
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentStep, snackAction } = useSelector(StepperSelectors.getStepperState);
  const currentStepInfo = getStepInfo(currentStep);
  const { push } = useHistory();
  const [saveChangesForm, setSaveChangesForm] = useState(false);
  const [showModalLoadError, setShowModalLoadError] = useState(false);
  const [hasShownError, setHasShownError] = useState(false);
  const { triggerSource, successOnSave, errorOnSave, saving, postingId, errorOnSaveMessage } = useSelector(
    SavePostingSelectors.getSavePostingState
  );
  const [postingIdFromUrl, isEdition, isDraftFromPanel, republish] = useQuery(useLocation(), [
    'postingId',
    'isEdition',
    'isDraftFromPanel',
    'republish'
  ]);
  const { dirtyForm } = useSelector(FormGuardSelectors.getFormGuardState);
  const isMobile = device === 'mobile';
  const pathToEditionView = '/publicador-profesionales/edition';

  useEffect(() => {
    if (saveChangesForm) {
      sendModalTrackingInfo('impression_modal', 'Modal volver');
    }
  }, [saveChangesForm]);

  useEffect(() => {
    if (
      currentStepInfo.stepId === 8 &&
      successOnSave &&
      triggerSource === triggerSources.SAVE_AND_CONTINUE &&
      !republish
    ) {
      handleRedirect('/publicador-profesionales/successfulPublication');
      return;
    }
    if (successOnSave) {
      dispatch(updateLastStep(String(currentStepInfo.stepId)));
      if (isEdition) {
        setCookieWithTTL('updateSuccessfully', 'true');
      }
      if (triggerSource === triggerSources.SAVE_AND_CONTINUE) {
        dispatch(SavePostingOperations.clearSaveState());
        // si es edicion envio a la vista de edicion al finalizar el guardado
        handleRedirect(isEdition ? pathToEditionView : currentStepInfo.nextStep?.path);
      }
      if (triggerSource === triggerSources.SAVE_PREVENT_MODAL) {
        // aca se realiza la accion del boton volver
        onHandleBack();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successOnSave, triggerSource]);

  useEffect(() => {
    if (
      errorOnSave &&
      (triggerSource === triggerSources.SAVE_AND_CONTINUE || triggerSource === triggerSources.SAVE_PREVENT_MODAL)
    ) {
      dispatch(
        StepperOperations.setSnackAction({
          action: 'savePostingError',
          result: 'ERROR',
          component: t(
            `professionalPubli.main.error.${
              errorOnSaveMessage === 'invalid_location' ? 'invalidLocation' : 'savePostingErrror'
            }`
          ),
          section: 'Publicador profesionales - Save Posting',
          preventDuplicateActions: true,
          preventStacking: true
        })
      );
    }
    if (errorOnSave) {
      sendCustomTrackingInfo({
        event: 'error_publicador',
        content_group: 'Publicador',
        error_type: 'Error de guardado',
        step: setStepTitle(currentStepInfo.stepId, isPlanEmpty)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorOnSave, triggerSource]);

  useEffect(() => {
    return () => {
      // Saneamiento del state que dispara el Snackbar:
      dispatch(StepperOperations.setSnackAction({ action: '', result: '' }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snackAction.result]);

  useEffect(() => {
    if (loadError && !hasShownError) {
      setShowModalLoadError(true); // Muestra el modal de error
      setHasShownError(true); // Marca que el error ya fue mostrado
      resetInfo(); // Resetea la información relacionada con el error
    }
  }, [loadError, hasShownError, resetInfo]);

  function onHandleBackWithSavePrevent() {
    if (dirtyForm) {
      return setSaveChangesForm(true);
    }
    onHandleBack();
  }

  function onHandleBack(withSnackbar = false) {
    if (withSnackbar) {
      setCookieWithTTL('leaveStepWithoutSaveChanges', 'true');
    }
    if (currentStepInfo.isFirstStep) {
      dispatch(FormGuardOperations.setDirtyForm(false));

      if (isDraftFromPanel && currentStep === 1) {
        return push('/avisos');
      }
      if (!isEdition) {
        resetInfo();
        // si es el primer paso tengo que enviar a la vista de seleccion de que se va a publicar.
        return push('/publicador-profesionales');
      }
    }
    // si es edicion envio a la vista de edicion
    handleRedirect(isEdition ? pathToEditionView : currentStepInfo.prevStep?.path);
  }

  function onHandleSubmit(event) {
    dispatch(SavePostingOperations.setSaveSource(triggerSources.SAVE_AND_CONTINUE));
    event.preventDefault();
    if (!errorOnSave && !saving) {
      adaptStepDataAndSendTrackinInfo(
        currentStepInfo.stepId,
        postingIdFromUrl,
        isEdition
          ? 'Guardar'
          : currentStepInfo.stepId === 8
          ? isPlanEmpty
            ? 'Guardar como borrador'
            : 'Publicar'
          : 'Continuar',
        selectedPropertyType || mainData.formValues.propertyTypeName,
        selectedOperationType || mainData.formValues.operationType,
        isEdition,
        planName,
        isDraftFromPanel,
        isPlanEmpty,
        selectedPropertySubType || mainData.formValues.propertySubTypeName
      );
    }
    onSubmit();
  }

  /**
   * Maneja la redirección a una nueva ruta preservando parámetros de query específicos
   * y limpiando el estado del formulario para evitar warnings de navegación.
   *
   * @function handleRedirect
   * @param {string} path - La ruta de destino a la cual redirigir (ej: '/publicador-profesionales/main')
   * @returns {void} No retorna valor, ejecuta la navegación directamente
   */
  function handleRedirect(path) {
    dispatch(FormGuardOperations.setDirtyForm(false));
    const id = postingIdFromUrl || postingId;
    const queryParams = [];
    if (id) queryParams.push(`postingId=${id}`);
    if (isDraftFromPanel && isDraftFromPanel !== 'false') queryParams.push(`isDraftFromPanel=${isDraftFromPanel}`);
    const finalUrl = `${path}${queryParams.length > 0 ? `?${queryParams.join('&')}` : ''}`;
    push(finalUrl);
  }

  return (
    <>
      <CustomPrompt when={dirtyForm} isMobile={isMobile} isEdition={isEdition} />
      <Container>
        {saveChangesForm && (
          <Modal>
            <SaveChangeForm
              bottomModal={isMobile}
              data-publi={`saveChangeModal-${id}`}
              openModal={saveChangesForm}
              onClose={() => {
                setSaveChangesForm(false);
              }}
              handleRedirect={() => {
                sendModalTrackingInfo('click_modal', 'Modal volver', 'Volver');
                onHandleBack();
              }}
              handleKeepEditing={() => {
                setSaveChangesForm(false);
                sendModalTrackingInfo('click_modal', 'Modal volver', 'Continuar editando');
              }}
              title={t('professionalPubli.formGuardModal.back.title')}
              description={t(`professionalPubli.formGuardModal.description${isBrasilSite ? '.s' : ''}`)}
              exitButtonText={t('professionalPubli.formGuardModal.back.exit')}
              keepButtonText={t('professionalPubli.formGuardModal.keep')}
            />
          </Modal>
        )}
        {showModalLoadError && (
          <Modal>
            <LoadingError
              openModal={showModalLoadError}
              onClose={() => {
                setShowModalLoadError(false);
                setHasShownError(false);
              }}
              device={device}
              sendCustomTrackingInfo={sendCustomTrackingInfo}
            />
          </Modal>
        )}
        <ButtonContainer device={getDevice()} isEdition={isEdition}>
          {isEdition ? (
            <BackButton
              isVisible={showBackButton}
              data-publi={`buttonPanel-${id}-button-back`}
              onClick={() => {
                adaptStepDataAndSendTrackinInfo(
                  currentStepInfo.stepId,
                  postingIdFromUrl,
                  'Cancelar',
                  mainData.formValues.propertyTypeName,
                  mainData.formValues.operationType,
                  isEdition,
                  planName,
                  isDraftFromPanel,
                  isPlanEmpty,
                  mainData.formValues.propertySubTypeName
                );
                onHandleBack(true);
              }}
              disabled={saving || !enableBackButton}
            >
              {t('professionalPubli.cta.cancel')}
            </BackButton>
          ) : (
            <BackButton
              isVisible={showBackButton}
              data-publi={`buttonPanel-${id}-button-back`}
              onClick={() => {
                adaptStepDataAndSendTrackinInfo(
                  currentStepInfo.stepId,
                  postingIdFromUrl,
                  'Volver',
                  mainData.formValues.propertyTypeName,
                  mainData.formValues.operationType,
                  isEdition,
                  planName,
                  isDraftFromPanel,
                  isPlanEmpty,
                  mainData.formValues.propertySubTypeName
                );
                onHandleBackWithSavePrevent();
              }}
              disabled={saving || !enableBackButton}
            >
              <Icon name='chevronLeft' width={20} height={20} colorFill='black' />
              {t('professionalPubli.cta.back')}
            </BackButton>
          )}
          <Submit
            data-publi={`buttonPanel-${id}-button-save`}
            disabled={saving || (isPlanEmpty && isEdition) || disabledSubmit}
            id='save'
            form={id}
            type='submit'
            onClick={onHandleSubmit}
            device={getDevice()}
          >
            {republish === 'true'
              ? t('professionalPubli.cta.republish')
              : isEdition
              ? t(`professionalPubli.cta.onlySave`)
              : btnSubmitText || t(`professionalPubli.cta.continue`)}
          </Submit>
        </ButtonContainer>
      </Container>
    </>
  );
};

export default ButtonPanel;
